import React, { useEffect, useRef, useState, useCallback } from 'react';
import '../styles/Kanban.css';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { formatAUD } from '../../../utils/formatCurrency';
import { toast } from 'react-toastify';
import { parseLeadSource } from '../utils/leadSource';

const STAGE_DOTS = [
  '#94a3b8',
  '#f59e0b',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#22c55e',
  '#ef4444',
];

function parseValue(v) {
  return Number(String(v || '').replace(/[^0-9.]/g, '')) || 0;
}

function leadScore(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'approve') return { score: 87, temp: 'Hot', tone: 'hot' };
  if (s === 'assigned') return { score: 72, temp: 'Warm', tone: 'warm' };
  if (s === 'cancel') return { score: 35, temp: 'Cold', tone: 'cold' };
  return { score: 54, temp: 'Moderate', tone: 'moderate' };
}

export default function Kanban() {
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState(null);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const draggingRef = useRef(false);

  const fetchStages = useCallback(async (pid) => {
    const query = `
      query StagesForPipeline($id: String!) {
        findAllStagesOfGivenPipeline(id: $id) {
          id
          name
          order
          booking {
            id
            fullName
            email
            phone
            address
            message
            status
            estimatedPRice
            bookingFrequency
            recordType
            createdAt
          }
        }
      }
    `;
    const data = await graphqlRequest(query, { id: pid });
    const stages = data?.findAllStagesOfGivenPipeline ?? [];
    const formatted = [...stages]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((stage) => ({
        id: stage.id,
        title: stage.name,
        order: stage.order,
        items: (stage.booking ?? [])
          .filter((b) => !b.recordType || String(b.recordType).toUpperCase() === 'LEAD')
          .map((b) => ({
            id: b.id,
            clientName: b.fullName,
            contactEmail: b.email,
            contactNumber: b.phone,
            status: b.status,
            value: parseValue(b.estimatedPRice),
            source: parseLeadSource(b.bookingFrequency),
            createdAt: b.createdAt,
          })),
      }));
    setColumns(formatted);
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError('');
      try {
        const pipelineData = await graphqlRequest(`
          query { findAllPipeline { id name order } }
        `);
        const nextPipelines = [...(pipelineData?.findAllPipeline ?? [])].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0),
        );
        setPipelines(nextPipelines);
        if (nextPipelines.length === 0) {
          setError('No pipeline found. Create a pipeline first.');
          setColumns([]);
          return;
        }
        const pid = nextPipelines[0].id;
        setPipelineId(pid);
        await fetchStages(pid);
      } catch (err) {
        console.error('Failed to load pipeline stages:', err);
        setError(err?.message || 'Failed to load pipeline stages');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [fetchStages]);

  useEffect(() => {
    if (!pipelineId) return undefined;
    const refresh = () => {
      if (draggingRef.current) return;
      fetchStages(pipelineId);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('lead:changed', refresh);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 30000);
    return () => {
      window.removeEventListener('lead:changed', refresh);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [fetchStages, pipelineId]);

  const handlePipelineChange = async (event) => {
    const pid = event.target.value;
    setPipelineId(pid);
    setLoading(true);
    setError('');
    try {
      await fetchStages(pid);
    } catch (err) {
      console.error('Failed to load pipeline stages:', err);
      setError(err?.message || 'Failed to load pipeline stages');
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    const sourceCol = columns.find((c) => c.id === source.droppableId);
    const destCol = columns.find((c) => c.id === destination.droppableId);
    if (!sourceCol || !destCol) return;

    const previous = columns;
    const sourceItems = Array.from(sourceCol.items);
    const [moved] = sourceItems.splice(source.index, 1);
    const destItems =
      source.droppableId === destination.droppableId
        ? sourceItems
        : Array.from(destCol.items);
    destItems.splice(destination.index, 0, moved);

    setColumns((cols) =>
      cols.map((c) => {
        if (c.id === source.droppableId) return { ...c, items: sourceItems };
        if (c.id === destination.droppableId) return { ...c, items: destItems };
        return c;
      }),
    );

    try {
      const mutation = `
        mutation MoveLead($input: CreateStageBooking!) {
          UpdateItemInsideStage(createStageBooking: $input) { id }
        }
      `;
      await graphqlRequest(mutation, {
        input: {
          pipelineId,
          stageId: destination.droppableId,
          bookingId: moved.id,
        },
      });
    } catch (err) {
      console.error('Failed to move lead between stages:', err);
      toast.error(err?.message || 'Failed to move lead');
      setColumns(previous);
    }
  };

  const columnValue = (items) =>
    items.reduce((sum, lead) => sum + Number(lead.value || 0), 0);

  if (loading && columns.length === 0) {
    return <p className="lead-summary">Loading pipeline stages…</p>;
  }

  if (error) {
    return <p className="lead-summary">{error}</p>;
  }

  return (
    <div className="lm-kanban-page">
      <div className="sales-report-box-kanban">
        {pipelines.length > 1 ? (
          <div className="lm-kanban-toolbar">
            <label>
              Pipeline
              <select
                value={pipelineId || ''}
                onChange={handlePipelineChange}
                disabled={loading}
              >
                {pipelines.map((pipeline) => (
                  <option key={pipeline.id} value={pipeline.id}>
                    {pipeline.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : null}
        <DragDropContext
          onDragStart={() => {
            draggingRef.current = true;
          }}
          onDragEnd={async (result) => {
            draggingRef.current = false;
            await onDragEnd(result);
          }}
        >
          <div className="horizontal-scroll">
            {columns.map((stage, idx) => (
              <Droppable
                key={stage.id}
                droppableId={stage.id}
                ignoreContainerClipping
              >
                {(provided) => (
                  <div className="opportunity-card">
                    <div className="stage-column-header">
                      <div className="lm-kanban-col-title">
                        <span
                          className="lm-kanban-dot"
                          style={{ background: STAGE_DOTS[idx % STAGE_DOTS.length] }}
                        />
                        <p className="opportunity">{stage.title}</p>
                      </div>
                      <p className="deal-count">
                        {stage.items.length} lead{stage.items.length !== 1 ? 's' : ''}
                        {columnValue(stage.items) > 0 && (
                          <span className="lm-kanban-value">
                            {' '}· {formatAUD(columnValue(stage.items))}
                          </span>
                        )}
                      </p>
                    </div>
                    <div
                      className="stage-cards-scroll"
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {stage.items.map((lead, index) => {
                        const { score, temp, tone } = leadScore(lead.status);
                        return (
                          <Draggable
                            key={lead.id}
                            draggableId={lead.id}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                className="company-item lm-lead-card"
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <div className="lm-lead-card-top">
                                  <span className={`lm-score lm-score--${tone}`}>
                                    {score} · {temp}
                                  </span>
                                </div>
                                <strong className="lm-lead-name">{lead.clientName}</strong>
                                <p className="lm-lead-contact">{lead.contactEmail}</p>
                                {lead.value > 0 && (
                                  <p className="lm-lead-value">{formatAUD(lead.value)}</p>
                                )}
                                <p className="lm-lead-meta">
                                  {lead.source}
                                </p>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                      {stage.items.length === 0 && (
                        <p className="lm-kanban-empty">No leads</p>
                      )}
                    </div>
                  </div>
                )}
              </Droppable>
            ))}
          </div>
        </DragDropContext>
      </div>
    </div>
  );
}
